
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateUser } from '@/hooks/useCurrentUser';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/ui/page-header';
import { toast } from '@/hooks/use-toast';

const ProfileEdit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const updateUser = useUpdateUser();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    email: '',
    phone: '',
    profilePhoto: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        nickname: user.nickname || '',
        email: user.email || '',
        phone: '',
        profilePhoto: ''
      });
    }
  }, [user]);

  const handleSave = async () => {
    if (user) {
      try {
        await updateUser.mutateAsync({
          firstName: formData.firstName,
          lastName: formData.lastName,
          nickname: formData.nickname,
          email: formData.email
        });
        toast({
          title: "Profile updated",
          description: "Your profile has been updated successfully.",
        });
        navigate('/updates');
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to update profile. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleBack = () => {
    navigate('/updates');
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          profilePhoto: e.target?.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-20 safe-area-inset">
      <PageHeader 
        title="Edit Profile"
        variant="back-with-title"
        onBack={handleBack}
      />

      {/* Content */}
      <div className="flex-1 flex flex-col justify-start px-6 pt-8 sm:pt-16 md:pt-24">
        <div className="mb-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-black mb-6 leading-tight">
            Edit your profile
          </h2>
          <p className="text-lg sm:text-xl text-[#666666] mb-8 leading-relaxed">
            Update your information and photo to personalize your experience.
          </p>
        </div>

        {/* Profile Photo */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
              {formData.profilePhoto ? (
                <img 
                  src={formData.profilePhoto} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <Camera className="h-8 w-8 text-gray-400" />
              )}
            </div>
            <label htmlFor="photo-upload" className="absolute bottom-0 right-0 bg-black text-white p-1 rounded-full cursor-pointer">
              <Camera className="h-4 w-4" />
            </label>
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-6">
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              First Name
            </label>
            <Input
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              placeholder="Enter your first name"
              className="h-12 sm:h-14 rounded-lg text-base sm:text-lg"
            />
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Last Name
            </label>
            <Input
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              placeholder="Enter your last name"
              className="h-12 sm:h-14 rounded-lg text-base sm:text-lg"
            />
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Nickname
            </label>
            <Input
              value={formData.nickname}
              onChange={(e) => setFormData(prev => ({ ...prev, nickname: e.target.value }))}
              placeholder="How would you like to be called?"
              className="h-12 sm:h-14 rounded-lg text-base sm:text-lg"
            />
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Email
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Enter your email address"
              className="h-12 sm:h-14 rounded-lg text-base sm:text-lg"
            />
          </div>

          <div>
            <label className="block text-lg font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="Enter your phone number"
              className="h-12 sm:h-14 rounded-lg text-base sm:text-lg"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8">
          <Button
            onClick={handleSave}
            className="w-full bg-black text-white hover:bg-[#666666] h-12 sm:h-14 rounded-lg text-base sm:text-lg font-medium min-touch-target touch-manipulation"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileEdit;
